import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard, CheckSquare, Users, Upload,
  BarChart3, Settings, CreditCard,
  Building2, CalendarDays, Shield, GraduationCap, Inbox, FolderUp, LineChart, Columns3, Send, Printer, Sparkles
} from 'lucide-react';
import { useAuthStore, resolveDeadlineTimeZone } from '../../store';
import { useMyPracticeAccess, type PracticeKeyedKey } from '../../lib/capabilities';
import { rankOf } from '../../lib/rank';
import PracticeSwitcher from './PracticeSwitcher';
import ModeToggle from './ModeToggle';
import TopNav, { type TopNavNode } from './TopNav';
import UserMenu from './UserMenu';
import NotificationBell from '../ui/NotificationBell';
// Phase 2.B — legacy OnboardingWizard auto-show is disabled; the new wizard
// is a full-screen route at /onboarding (App.tsx redirects via PracticeStatus).
// Old import kept commented for reference; safe to delete after Phase 2 ships.
// import OnboardingWizard from './OnboardingWizard';
import SandboxBanner from '../sandbox/SandboxBanner';
import BackfillBanner from '../backfill/BackfillBanner';
import PolicyUpdateBanner from '../compliance/PolicyUpdateBanner';
import PendingAssignmentBanner from './PendingAssignmentBanner';
import PracticeLostBanner from './PracticeLostBanner';
import { removePracticeLostRecord } from '../../lib/practiceLostRecord';
import { useNotifications } from '../../hooks';
import { setDeadlineTimeZone } from '../../utils/taskStatus';
import { tasksApi, patientsApi, appointmentsApi, modulesApi } from '../../lib/api';

// §15/§10 — the module the ask gate's first switch reads (backend constants/moduleKeys.ts
// AI_ASSIST_MODULE_KEY). Declared locally, as AdminPage.tsx and ModuleSelectionStep.tsx already do.
const AI_ASSIST_MODULE_KEY = 'ai_assist';

/** One row of GET /modules/practice — only the two fields the nav's module gate reads. */
type PracticeModuleRow = { is_enabled?: boolean; module?: { module_key?: string } };

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['all'] },
  { path: '/tasks', icon: CheckSquare, label: 'Tasks', roles: ['all'] },
  { path: '/queues', icon: Columns3, label: 'Queue', roles: ['all'] },
  { path: '/patients', icon: Users, label: 'Patients', roles: ['all'] },
  { path: '/appointments', icon: CalendarDays, label: 'Appts', roles: ['all'] },
  { path: '/training', icon: GraduationCap, label: 'Training', roles: ['all'] },
  { path: '/fax', icon: Inbox, label: 'Inbox', roles: ['all'] },
  { path: '/send-fax', icon: Send, label: 'Send', roles: ['all'] },
  // AI Assist — no role name reaches this surface; it is gated below on the CAPABILITY grant AND on
  // the practice having the module enabled (roles:['all'] leaves the role filter out entirely).
  { path: '/ai-assist', icon: Sparkles, label: 'AI Assist', roles: ['all'] },
  // fax.7 — manager ops dashboards (accuracy / aging / coverage). HELD (DO NOT DEPLOY) —
  // de-wired for the F10 deploy 2026-06-16; re-enable after re-targeting to the task surface.
  // { path: '/fax-insights', icon: LineChart, label: 'Fax Insights', roles: ['platform_admin','platform_account_manager','practice_admin'] },
  { path: '/imports', icon: Upload, label: 'Imports', roles: ['platform_admin','platform_account_manager','platform_operator','practice_admin','practice_manager'] },
  // Bulk eligibility-PDF upload — internal platform tool (has its own practice
  // selector, so it stays visible in Platform mode unlike the PHI nav items).
  { path: '/eligibility-uploads', icon: FolderUp, label: 'Eligibility', roles: ['platform_admin','platform_account_manager','platform_operator'] },
  { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['platform_admin','platform_account_manager','platform_operator','platform_supervisor','practice_admin','practice_manager'] },
  { path: '/admin', icon: Settings, label: 'Admin', roles: ['platform_admin','platform_account_manager','practice_admin'] },
  { path: '/audit', icon: Shield, label: 'Audit', roles: ['platform_admin','platform_operator','platform_supervisor','practice_admin'] },
  { path: '/billing', icon: CreditCard, label: 'Billing', roles: ['platform_admin','practice_admin','practice_manager'] },
];

interface AppShellProps {
  children: React.ReactNode;
}

export default function AppShell({ children }: AppShellProps) {
  const { logout } = useAuth0();
  const queryClient = useQueryClient();
  const { user, currentPractice, isPlatformUser, logout: storeLogout, platformMode, assignedPractices,
    isAccountManager, isPlatformAdmin, setPlatformMode } = useAuthStore();

  // Wave 3c (Decision 1) — a (pure) account_manager is ALWAYS in-practice: their only navigation is the
  // assigned-practice switcher; the Platform/Practice mode toggle is hidden. Force platformMode false so
  // a persisted `true` can never strand them in a near-empty Platform view. A platform_admin (the god-set
  // holder, even if also an account_manager) keeps the platform view.
  const isPureAccountManager = isAccountManager() && !isPlatformAdmin();
  useEffect(() => {
    if (isPureAccountManager && platformMode) setPlatformMode(false);
  }, [isPureAccountManager, platformMode, setPlatformMode]);

  // A DATE deadline (hipaa_deadline / due_date) is "the LAST day to comply" on the PRACTICE's
  // calendar, which is the basis the server judges it on (deadlineClock.localTodaySql). Set
  // during render, not in an effect: the first paint already reads it. The resolution — the
  // selected practice's zone, else this user's practice zone, else the default the SERVER
  // resolved and shipped on /auth/me — lives in the store so this and DoctorReviewPage (which
  // mounts OUTSIDE the shell) can never disagree. It is never the viewer's own zone.
  const deadlineZone = useAuthStore(resolveDeadlineTimeZone);
  setDeadlineTimeZone(deadlineZone);
  const location = useLocation();

  // ── Prefetch on nav hover ─────────────────────────────────────────────────
  // When user hovers a nav item, start fetching that page's data in the background.
  // By the time they click, data is already in cache — navigation feels instant.
  const prefetchPage = useCallback((path: string) => {
    const defaultParams = { limit: 50, offset: 0, sort_field: 'due_date', sort_order: 'ASC' };
    const today = new Date().toISOString().split('T')[0];

    switch (path) {
      case '/tasks':
        queryClient.prefetchQuery({
          queryKey: ['tasks-list', defaultParams],
          queryFn: () => tasksApi.getAll(defaultParams).then(r => r.data),
          staleTime: 0,
        });
        break;
      case '/patients':
        queryClient.prefetchQuery({
          queryKey: ['patients', { limit: 50, offset: 0 }],
          queryFn: () => patientsApi.getAll({ limit: 50, offset: 0 }).then(r => r.data),
          staleTime: 0,
        });
        break;
      case '/appointments':
        queryClient.prefetchQuery({
          queryKey: ['appointments-list', today, today, [], [], 0],
          queryFn: () => appointmentsApi.getAll({ limit: 50, offset: 0, date_from: today, date_to: today }).then(r => r.data),
          staleTime: 0,
        });
        break;
    }
  }, [queryClient]);

  // Bootstrap notifications on mount
  useNotifications();

  // Phase 2.B — legacy auto-show wizard is disabled. New wizard lives at /onboarding
  // and is reached via App.tsx routing based on practices.status. The state hook
  // and dismiss handler below are retained as no-ops to avoid breaking other
  // call sites; safe to remove after Phase 2 ships.
  const [showWizard] = useState(false);
  void useState; void user;

  // Phase A3 — in Platform mode, suppress PHI-adjacent nav items. They make no
  // sense without a single-practice context; the user can drill in via Tasks
  // and the practice context will switch automatically.
  // '/ai-assist' joins them for the same reason and one more: the server's gate refuses an ask with
  // no practice selected (PRACTICE_REQUIRED), so in Platform mode the entry could only ever lead to
  // a refusal.
  const PHI_PATHS_IN_PRACTICE_MODE_ONLY = ['/patients', '/appointments', '/imports', '/ai-assist'];
  // Inverse rule: internal platform tools only make sense in Platform view —
  // hide them while a platform user is browsing in Practice mode (avoids the
  // tool appearing alongside a single practice's PHI nav). Authorization is
  // enforced server-side regardless; this is visibility/UX only.
  const PLATFORM_MODE_ONLY_PATHS = ['/eligibility-uploads'];
  // Wave 3c — match nav entries against the roles[] UNION (mirrors the backend; byte-equivalent in
  // prod where roles=[role]) so a multi-role user's nav reflects every role they hold.
  const grantedRoles = user ? (user.roles ?? (user.role ? [user.role] : [])) : [];
  // Surfaces that no ROLE NAME reaches — a PRACTICE-KEYED CAPABILITY does (user_grants
  // grant_type='capability' at THIS practice). Same shape as the two mode lists above: a path list
  // consulted inside the one filter, not a second gating mechanism. Keyed on the SAME capability
  // string the page and the server's services/aiAssist/gate.ts read, and answered by
  // useMyPracticeAccess (GET /api/access/me at the selected practice, platform admin included), so a
  // tick held at practice A no longer shows the entry at practice B. Visibility/UX only, exactly like
  // the roles filter; the server's gate stays the authority.
  const CAPABILITY_REQUIRED_PATHS: Record<string, PracticeKeyedKey> = { '/ai-assist': 'ai_assist.use' };
  const { canUsePracticeKey } = useMyPracticeAccess();
  // …and the capability is only ONE of the server ask gate's three switches. The other two are a
  // selected practice (the Platform-mode rule above) and the PRACTICE having the `ai_assist` MODULE
  // enabled — services/aiAssist/gate.ts requireAiAssistEnabled, which answers 403 MODULE_DISABLED.
  // §10 made that switch platform-only for pricing reasons, so "the module is off while users still
  // hold the grant" is a state the founder's own ruling creates; without this leg the menu would
  // advertise a screen the server refuses on arrival.
  //
  // FAIL CLOSED, like the server's own switch: the entry is absent until a row is KNOWN on. While
  // the list loads, if the read fails, and if the practice has no `ai_assist` row, `aiModuleEnabled`
  // is false. The request is not issued at all in Platform mode (there is no practice to read
  // modules for) or for a user without the capability — a gate must not leak a call whose answer it
  // has already decided. The key is the one the rest of the app already uses, so this shares that
  // cache rather than opening a second one.
  const mayHoldAiAssist = !platformMode && canUsePracticeKey(CAPABILITY_REQUIRED_PATHS['/ai-assist']);
  const practiceModules = useQuery({
    queryKey: ['practice-modules'],
    enabled: mayHoldAiAssist,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const res = await modulesApi.getPracticeModules();
      return res.data.data as PracticeModuleRow[];
    },
  });
  // ⚠️ ONE HONEST WINDOW, stated rather than implied: ['practice-modules'] is not practice-keyed
  // (the shape the whole app already uses). On a practice switch PracticeSwitcher's
  // invalidateQueries() refetches it, and for that one request react-query still serves the PREVIOUS
  // practice's rows. (The capability leg no longer shares that window: its read is keyed by the
  // practice.) Same answer as ever: visibility only, and the server refuses regardless.
  const aiModuleEnabled = (practiceModules.data ?? []).some(
    (row) => row?.module?.module_key === AI_ASSIST_MODULE_KEY && row?.is_enabled === true,
  );
  const allowedNav = navItems.filter((item) => {
    const roleOk = item.roles.includes('all') || grantedRoles.some((r) => item.roles.includes(r));
    if (!roleOk) return false;
    const capabilityKey = CAPABILITY_REQUIRED_PATHS[item.path];
    if (capabilityKey && !canUsePracticeKey(capabilityKey)) return false;
    if (item.path === '/ai-assist' && !aiModuleEnabled) return false;
    if (platformMode && PHI_PATHS_IN_PRACTICE_MODE_ONLY.includes(item.path)) return false;
    if (!platformMode && PLATFORM_MODE_ONLY_PATHS.includes(item.path)) return false;
    return true;
  });

  // Present the two fax surfaces as one "Fax" menu (Inbox / Send). DISPLAY only —
  // allowedNav is still gated per-item above, so the group carries exactly the fax
  // children the user is allowed to see (and collapses to a plain link if only one).
  const FAX_MEMBER_PATHS = ['/fax', '/send-fax'];
  const faxChildren = allowedNav.filter((i) => FAX_MEMBER_PATHS.includes(i.path));
  const navDisplay: TopNavNode[] = [];
  let faxPlaced = false;
  for (const item of allowedNav) {
    if (FAX_MEMBER_PATHS.includes(item.path)) {
      if (faxPlaced) continue;
      faxPlaced = true;
      if (faxChildren.length === 1) {
        navDisplay.push({ kind: 'leaf', path: faxChildren[0].path, icon: faxChildren[0].icon, label: faxChildren[0].label });
      } else {
        navDisplay.push({
          kind: 'group', key: 'fax', icon: Printer, label: 'Fax',
          children: faxChildren.map((c) => ({ path: c.path, icon: c.icon, label: c.label })),
        });
      }
    } else {
      navDisplay.push({ kind: 'leaf', path: item.path, icon: item.icon, label: item.label });
    }
  }

  const initials = `${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`;
  const displayName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();

  // HIPAA — wipe all cached data on logout so no stale PHI survives. Preserved
  // VERBATIM from the old sidebar footer; relocated into the UserMenu dropdown.
  const handleSignOut = useCallback(() => {
    // ai.346 DR1 (L5-1) — the lost-practice notice belongs to this user: never shown to the next one on the tab.
    removePracticeLostRecord();
    storeLogout();
    queryClient.clear();
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [storeLogout, queryClient, logout]);

  // ai.346 DR1 (L9-2) — the practice-lost notice's Dismiss parks focus on <main> (tabIndex -1).
  const mainRef = useRef<HTMLElement>(null);

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
      {/* Phase 2.B — legacy auto-show wizard disabled. New wizard at /onboarding. */}
      {showWizard && null}

      {/* ── TOP BAR ──────────────────────────────────────────────────────
          Phase-1 nav redesign: the former left sidebar is now a top icon bar.
          The nav-VISIBILITY model is UNCHANGED — `allowedNav` (the roles filter +
          the two mode overrides + the pure-account-manager force computed above)
          is simply relocated into <TopNav>. h-14 (56px) is the anchor the three
          detail drawers offset against (top-14). */}
      <header className="flex items-center gap-2 h-14 bg-white border-b border-gray-200 px-4 flex-shrink-0 shadow-[0_1px_2px_rgba(17,24,39,0.04)]">
        <Link
          to="/dashboard"
          aria-label="RCMTask — Dashboard"
          className="flex items-center gap-2 mr-1.5 flex-shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C94B1F] focus-visible:ring-offset-1"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-[9px] bg-[#C94B1F] shadow-[0_1px_2px_rgba(168,61,24,0.35)]">
            {/* Brand mark — three stacked rows: the shape of a work queue / task list. */}
            <svg viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true" className="h-[17px] w-[17px]">
              <rect x="4" y="5" width="16" height="3.4" rx="1.7" />
              <rect x="4" y="10.3" width="12" height="3.4" rx="1.7" />
              <rect x="4" y="15.6" width="8" height="3.4" rx="1.7" />
            </svg>
          </span>
          <span className="text-[17px] font-extrabold tracking-tight text-gray-900">RCMTask</span>
        </Link>

        <TopNav items={navDisplay} pathname={location.pathname} onPrefetch={prefetchPage} />

        <div className="ml-auto flex items-center gap-3 flex-shrink-0 min-w-0">
          {/* Phase A3 — Practice/Platform mode toggle (platform users only).
              Wave 3c (Decision 1) — hidden for a pure account_manager (always in-practice). */}
          {isPlatformUser() && !isPureAccountManager && <ModeToggle />}
          {/* PracticeSwitcher only in Practice mode (platform users) — hidden
              in Platform mode since there's no single-practice context. */}
          {isPlatformUser() && !platformMode && <PracticeSwitcher />}
          {/* Wave 3c — accountability + orientation cue: an account_manager is administering someone
              else's practice as platform staff (not their own). Distinct chip, shown when in a practice. */}
          {isPureAccountManager && currentPractice && (
            <div className="flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-sm text-emerald-800 min-w-0 whitespace-nowrap">
              <Shield className="w-4 h-4 flex-shrink-0" />
              <span>Administering <span className="font-semibold truncate max-w-[9rem] inline-block align-bottom">{currentPractice.name}</span> as platform staff</span>
            </div>
          )}
          {/* Platform-mode indicator (replaces the switcher visually). */}
          {isPlatformUser() && platformMode && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Building2 className="w-4 h-4" />
              <span>Platform view — {assignedPractices.length || 0} {assignedPractices.length === 1 ? 'practice' : 'practices'}</span>
            </div>
          )}
          {/* Practice user: an ORG-LEVEL user (T6a — /auth/me returns >1 assigned practice in
              their org) gets the PracticeSwitcher to move between their org's clinics; everyone
              else sees a static practice name. The switcher carries no authz — each X-Practice-ID
              is re-validated server-side against the user's assignments. The switcher shows even
              when no practice is selected (its amber "Select practice..." state; ai.346 S1 A). */}
          {!isPlatformUser() && (
            assignedPractices.length > 1
              ? <PracticeSwitcher />
              : currentPractice && (
                <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                  <Building2 className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium truncate max-w-[12rem]">{currentPractice.name}</span>
                </div>
              )
          )}
          <NotificationBell />
          <UserMenu
            initials={initials}
            name={displayName}
            rank={user ? rankOf(user) : ''}
            onSignOut={handleSignOut}
          />
        </div>
      </header>

      {/* ai.346 — one-time notice after a refused practice (403 PRACTICE_SWITCH_DENIED cleared it
          and reloaded). Self-hides when there is nothing to report. */}
      <PracticeLostBanner mainRef={mainRef} />

      {/* Phase 2.B — sandbox / pre-BAA banner. Renders only when practice
          status ∈ {onboarding, sandbox, pending_baa}. Self-hides otherwise. */}
      <SandboxBanner />

      {/* Phase 2.F — post-hoc backfill banner for legacy 'active' practices
          without a signed BAA. Self-hides for everyone else. */}
      <BackfillBanner />

      {/* Phase 3.B — policy-update banner when binder docs drifted to a newer
          template version. Self-hides when nothing is pending. */}
      <PolicyUpdateBanner />

      {/* Phase A2 — banner for platform users (account_manager/operator/
          supervisor) with zero practice assignments. Self-hides for everyone
          else (incl. platform_admin and practice users). */}
      <PendingAssignmentBanner />

      {/* Page content */}
      <main ref={mainRef} tabIndex={-1} className="flex-1 overflow-auto focus:outline-none">
        {children}
      </main>
    </div>
  );
}
